import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApplicationStep, AppRoutes } from '@sparrow-card/sparrow-card-constants';
import Loading from 'components/Loading';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useNavigateWithPreservedParams } from 'hooks/useNavigateWithPreservedParams';
import { type RootState } from 'stores';
import { getApplicationStepThunk } from 'thunks';

const routeToStepMapping: Partial<Record<AppRoutes, ApplicationStep>> = {
  [AppRoutes.Approval]: ApplicationStep.Approved,
  [AppRoutes.Decline]: ApplicationStep.Declined,
  [AppRoutes.Locked]: ApplicationStep.Locked,
  [AppRoutes.Review]: ApplicationStep.Review,
  [AppRoutes.Canceled]: ApplicationStep.Canceled,
  [AppRoutes.Verify]: ApplicationStep.Verify,
  [AppRoutes.DocumentVerification]: ApplicationStep.DocumentVerification,
};

const stepToRoutesMapping = {} as Partial<Record<ApplicationStep, AppRoutes>>;

for (const [route, step] of Object.entries(routeToStepMapping)) {
  if (!stepToRoutesMapping[step]) {
    stepToRoutesMapping[step] = route as AppRoutes;
  }
}

function checkIfStepIsValid(currentPath: string, fetchedStep: ApplicationStep): boolean {
  const expectedStep = routeToStepMapping[currentPath as AppRoutes];

  return expectedStep === fetchedStep;
}

const StepChecker: React.FC = () => {
  const navigate = useNavigateWithPreservedParams();
  const navigateRef = useRef(navigate);

  navigateRef.current = navigate;

  const location = useLocation();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const applicationId = useSelector<RootState, string | undefined>(
    (state) => state.application.applicationId,
  );
  const submissionId = useSelector<RootState, string | undefined>(
    (state) => state.application.submissionId,
  );

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!applicationId) {
      return;
    }

    let active = true;

    const validateStep = async () => {
      try {
        const { step } = await dispatchWithUnwrap(getApplicationStepThunk(applicationId));

        if (!active) {
          return;
        }

        const isStepValid = checkIfStepIsValid(location.pathname, step);

        if (!isStepValid) {
          const routeToNavigate = stepToRoutesMapping[step] || AppRoutes.Apply;

          navigateRef.current(routeToNavigate);
        }
      } catch (error) {
        if (active) {
          navigateRef.current(AppRoutes.NotFound);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void validateStep();

    return () => {
      active = false;
    };
  }, [applicationId, dispatchWithUnwrap, location.pathname]);

  if (!applicationId) {
    return <Navigate to={AppRoutes.Apply} replace />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return <Outlet context={{ applicationId, submissionId }} />;
};

export default StepChecker;
